import React from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

/**
 * The shared FC pin map — one component so the placement workspace's "FC map" card and the
 * Shipment Queue's hover popover (owner, 2026-09-02: "can a tooltip appear on the shipment queue
 * page that shows this on hover?") can never drift apart.
 *
 * Visual language matches the planner's FC Distribution Map: current stock is a solid blue dot,
 * inbound (confirmed, still in transit) is a solid violet dot, and a proposed/selected FC is an
 * orange ring — the ring composes over either dot, because one FC can be all three at once.
 * FCs the dictionary cannot place are listed under the map, never silently dropped.
 *
 * Pins come from buildFcMapPins (placementBasket.js). `selectedLabel` names the orange layer for
 * the surface ("Selected plans" on the workspace, "This plan" on the queue popover); `note`
 * renders as a small status line (e.g. "loading stock/inbound…" while the lazy layers arrive).
 */
export default function FcPinMap({ pins, unknown, heightPx = 360, selectedLabel = 'Selected plans', note = null }) {
    if (!pins.length && !unknown.length && !note) return null;
    const count = pred => pins.filter(pred).length + unknown.filter(pred).length;
    const stockCount = count(p => p.stockUnits != null);
    const inboundCount = count(p => p.inboundUnits != null);
    const selectedCount = count(p => p.selected);

    const describe = p => [
        p.fc + (p.city ? ` — ${p.city}${p.state ? `, ${p.state}` : ''}` : ''),
        p.stockUnits != null ? `${p.stockUnits} on hand` : null,
        p.inboundUnits != null ? `${p.inboundUnits} inbound` : null,
        p.selected ? 'in a selected plan' : null,
    ].filter(Boolean).join(' · ');

    return (
        <div className="bg-white rounded-md p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="text-sm font-semibold text-gray-700">FC map</h3>
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-full bg-blue-500 opacity-70"></span>
                        Current stock ({stockCount} FC{stockCount !== 1 ? 's' : ''})
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-full bg-violet-500 opacity-70"></span>
                        Inbound ({inboundCount} FC{inboundCount !== 1 ? 's' : ''})
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-full border-2 border-orange-500 bg-orange-100"></span>
                        {selectedLabel} ({selectedCount} FC{selectedCount !== 1 ? 's' : ''})
                    </span>
                </div>
            </div>
            {note && <div className="mb-2 text-xs text-gray-400 italic">{note}</div>}
            {pins.length > 0 && (
                <div className="relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" style={{ height: `${heightPx}px` }}>
                    <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 900 }}
                                   width={980} height={360} style={{ width: '100%', height: '100%' }}>
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) => geographies.map(geo => (
                                <Geography key={geo.rsmKey} geography={geo}
                                           fill="#e5e7eb" stroke="#ffffff" strokeWidth={0.75} />
                            ))}
                        </Geographies>
                        {pins.map(p => (
                            <Marker key={p.fc} coordinates={[p.lng, p.lat]}>
                                <title>{describe(p)}</title>
                                {(p.stockUnits != null || p.inboundUnits != null) && (
                                    <circle r={6}
                                            fill={p.stockUnits != null ? '#3b82f6' : '#8b5cf6'}
                                            fillOpacity={0.7} stroke="#ffffff" strokeWidth={1} />
                                )}
                                {p.selected && (
                                    <circle r={9} fill="none" stroke="#f97316" strokeWidth={2.5} />
                                )}
                                <text textAnchor="middle" y={-13}
                                      style={{ fontSize: 9, fontFamily: 'monospace', fill: '#374151' }}>
                                    {p.fc}
                                </text>
                            </Marker>
                        ))}
                    </ComposableMap>
                </div>
            )}
            {unknown.length > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                    Location unknown (not plotted):{' '}
                    {unknown.map(p => describe(p)).join(' · ')}
                </div>
            )}
        </div>
    );
}
